import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow,} from "@/components/ui/table"
import React, {useImperativeHandle, useMemo} from "react";
import VisibilitySensor from "@/components/ui/sensor";
import {formatIsoReadable} from "@/utils/dateUtils";
import {Badge} from "@/components/ui/badge";
import useWorkflowLogs from "@/query/workflowLog/useWorkflowLogs";
import {WorkflowLog, WorkflowLogStatus} from "@/model/Log";
import {Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger} from "@/components/ui/sheet";
import moment from "moment";
import {useQueryClient} from "@tanstack/react-query";
import WorkflowLogViewer from "@/view/workflowLog/component/WorkflowLogViewer";

interface WorkflowLogListProps {
    status?: WorkflowLogStatus
    workflowIds?: string[]
    groupId?: string
    onCopyParameter?: (parameter: any) => void
}

export interface WorkflowLogListRef {
    invalidate: () => void
}

export const WorkflowLogList = React.forwardRef<WorkflowLogListRef, WorkflowLogListProps>(({status, workflowIds, groupId, onCopyParameter}, ref) => {
    const queryClient = useQueryClient();
    const {data, hasNextPage, fetchNextPage, isLoading} = useWorkflowLogs({groupId, status, workflowIds});

    const logs = useMemo(() => {
        return data?.pages.map((page) => page.content).flat();
    }, [data])

    useImperativeHandle(ref, () => {
        return {
            invalidate: () =>  {
                queryClient.invalidateQueries(["workflow", "logs", groupId, workflowIds, status], {exact: true});
            },
        }
    }, [groupId, queryClient, status, workflowIds])


    const onScrollAtEnd = async () => {
        if (hasNextPage && !isLoading) {
            await fetchNextPage();
        }
    }

    return (
        <div className="flex min-h-0 h-full flex-col overflow-y-auto p-2">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[1%] text-center"></TableHead>
                        <TableHead className="w-[25%] text-center">Query</TableHead>
                        <TableHead className='w-[5%] text-center'>Status</TableHead>
                        <TableHead className='w-[10%] text-center'>User</TableHead>
                        <TableHead className="text-center">Chatbot Name</TableHead>
                        <TableHead className='text-center'>Created At</TableHead>
                        {onCopyParameter && <TableHead className='text-center'></TableHead>}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {logs?.map((log, index) => (
                        <Sheet key={`log-${log._id}`}>
                            <SheetTrigger asChild>
                                <TableRow key={log._id} role={'button'} className='border-0'>
                                    <>
                                        <TableCell className="font-medium text-center">{index + 1}</TableCell>
                                        <TableCell className="font-medium text-center truncate max-w-[350px]">{log.params?.query}</TableCell>
                                        <TableCell className="font-medium text-center">
                                            {statusBadge(log)}
                                        </TableCell>
                                        <TableCell className="font-medium text-center">
                                            {log.user.name}
                                        </TableCell>
                                        <TableCell className="font-medium text-center">
                                            {log.params?.chatbot_name}
                                        </TableCell>
                                        <TableCell className="font-medium text-center">
                                            {formatIsoReadable(log.createdAt)}
                                        </TableCell>
                                        {onCopyParameter && <TableCell
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                onCopyParameter(log.params)
                                            }}
                                            className="cursor-pointer hover:text-blue-500 text-center underline"
                                        >
                                            copy
                                        </TableCell>}
                                    </>
                                </TableRow>
                            </SheetTrigger>
                            <LogSheetContent log={log}/>
                        </Sheet>
                    ))}

                    {logs?.length === 0 &&
                        <TableRow className='border-0'><TableCell colSpan={4}>No result.</TableCell></TableRow>}

                    {!isLoading &&
                        <tr>
                            <td>
                                <VisibilitySensor onEnter={onScrollAtEnd}/>
                            </td>
                        </tr>
                    }
                </TableBody>
            </Table>
        </div>
    )
})

export const statusBadge = (log: WorkflowLog) => {
    if (log.status === 'done') {
        return <Badge variant="outline">Done</Badge>
    } else if (log.status === 'failed') {
        return <Badge variant="destructive">Failed</Badge>
    }
}

const LogSheetContent: React.FC<{ log: WorkflowLog }> = ({log}) => {
    const defaultLayout = useMemo(() => {
        const layout = localStorage.getItem("react-resizable-panels:layout:workflow-log")
        return layout ? JSON.parse(layout) : undefined
    }, [])

    const resultOutletHistory = log.operatorOutlets[log.resultOperatorId];
    const resultOutlet = Array.isArray(resultOutletHistory) ? resultOutletHistory.at(-1)! : resultOutletHistory;
    const executionTime = moment.utc(resultOutlet.endedAt).diff(moment.utc(log.createdAt), "milliseconds").toLocaleString("ko-KR");

    return (
        <SheetContent className="w-[90%] sm:w-[90%] sm:max-w-[90%] h-screen flex flex-col">
            <SheetHeader className='flex'>
                <SheetTitle className='flex items-center'>
                    {statusBadge(log)}
                </SheetTitle>
                <SheetDescription>
                    <span className='text-xs text-slate-500 mr-5'>Duration (ms): {executionTime}</span>
                    <span className='text-xs text-slate-500 mr-5'>Started At: {formatIsoReadable(log.startedAt)}</span>
                    <span className='text-xs text-slate-500 mr-5'>Ended At: {formatIsoReadable(resultOutlet.endedAt)}</span>

                </SheetDescription>
            </SheetHeader>

            <div className='rounded-xl border bg-card text-card-foreground shadow w-full h-full overflow-hidden'>
                <WorkflowLogViewer
                    className='grow'
                    log={log}
                    defaultLayout={defaultLayout}
                />
            </div>

            {/*<SheetFooter>*/}
            {/*    {job.status === 'running' &&*/}
            {/*        <Button type="submit" variant='destructive' onClick={interrupt}>*/}
            {/*            Interrupt*/}
            {/*        </Button>*/}
            {/*    }*/}
            {/*    <Button type="submit" variant='destructive' onClick={interrupt}>*/}
            {/*        Interrupt*/}
            {/*    </Button>*/}
            {/*    /!*<SheetClose asChild>*!/*/}
            {/*    /!*    <Button type="submit">Save changes</Button>*!/*/}
            {/*    /!*</SheetClose>*!/*/}
            {/*</SheetFooter>*/}
        </SheetContent>
    )
}



export default WorkflowLogList;